import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Users, Hash, Send, Copy, Mail, UserPlus, MessageCircle,
  CornerDownRight, Activity, ClipboardCheck, ShieldCheck, CheckCircle2, Clock, AlertTriangle, ArrowRight
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";

const TABS = [
  { k: "chat", label: "Chat", icon: MessageCircle },
  { k: "analytics", label: "Analytics", icon: Activity },
  { k: "assignments", label: "Assignments", icon: ClipboardCheck },
];

export default function Hubs() {
  const [allHubs, setAllHubs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("chat");
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const { user } = useAuth();

  const loadAll = useCallback(async () => {
    try {
      const r = await api.get("/teams/all-in-org");
      setAllHubs(r.data || []);
      if (!selected && r.data?.length) {
        const mine = r.data.find((h) => h.i_am_member);
        setSelected(mine || r.data[0]);
      }
    } catch (e) { /* ignore */ }
  }, [selected]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const canAssign = user?.org_role === "admin" || user?.org_role === "controller";

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col md:flex-row">
      {/* Mobile hub picker bar — only visible < md */}
      <div className="md:hidden bg-white border-b border-[#1a1a1a]/15 px-3 py-2 flex items-center gap-2">
        <button data-testid="mobile-hub-toggle" onClick={() => setMobileSidebar((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold border border-[#1a1a1a] px-2.5 py-1 rounded-full">
          <Users className="w-3 h-3" /> Hubs
        </button>
        <div className="flex-1 min-w-0 truncate font-display font-bold text-sm">
          {selected?.name || "Pick a hub"}
        </div>
      </div>

      {/* Sidebar — always visible on md+, drawer on mobile */}
      <aside data-testid="hubs-sidebar" className={`${mobileSidebar ? "flex" : "hidden"} md:flex w-full md:w-72 shrink-0 bg-white border-r border-[#1a1a1a]/15 flex-col md:h-full max-h-[60vh] md:max-h-none`}>
        <div className="p-4 border-b border-[#1a1a1a]/15">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// All hubs in your org</div>
          <div className="flex gap-1">
            <button data-testid="create-team-button" onClick={() => setCreateOpen(true)}
              className="flex-1 inline-flex items-center justify-center gap-1 bg-[#ff4500] text-white px-2 py-2 text-[11px] uppercase tracking-wider font-bold hover:bg-[#cc3500]">
              <Plus className="w-3 h-3" /> Create
            </button>
            <button data-testid="join-team-button" onClick={() => setJoinOpen(true)}
              className="flex-1 inline-flex items-center justify-center gap-1 border border-[#1a1a1a] text-[#1a1a1a] px-2 py-2 text-[11px] uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white">
              <Hash className="w-3 h-3" /> Code
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto scrollbar-thin">
          {allHubs.length === 0 && <div className="hatched py-10 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">No hubs in your organization yet.</div>}
          {allHubs.map((h) => (
            <button key={h.id} data-testid={`team-${h.id}`} onClick={() => { setSelected(h); setTab("chat"); setMobileSidebar(false); }}
              className={`w-full text-left px-4 py-3 border-b border-[#1a1a1a]/10 ${selected?.id === h.id ? "bg-[#1a1a1a] text-white" : "hover:bg-[#f0ede6]"}`}>
              <div className="font-display font-bold text-sm flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                <span className="truncate">{h.name}</span>
                {h.i_am_member && <span className="ml-auto text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1a8754] text-white">In</span>}
                {!h.i_am_member && h.i_requested && <span className="ml-auto text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#d6a900] text-white">Req</span>}
              </div>
              <div className={`text-[10px] font-mono uppercase tracking-wider mt-0.5 ${selected?.id === h.id ? "text-white/70" : "text-[#1a1a1a]/60"}`}>
                {h.members_count} member{h.members_count === 1 ? "" : "s"} · {h.description || "Hub channel"}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col bg-[#f7f5f2] min-w-0 min-h-0">
        {selected ? (
          <>
            <header className="bg-white border-b border-[#1a1a1a]/15 px-3 md:px-6 py-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Hub</div>
                  <div className="font-display font-black text-base md:text-lg truncate" data-testid="team-name-heading">{selected.name}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {selected.i_am_member ? (
                    <>
                      {canAssign && <button data-testid="hub-assign-open" onClick={() => setAssignOpen(true)}
                        className="btn-pill btn-pill-secondary text-[11px]"><ClipboardCheck className="w-3 h-3" /> <span className="hidden sm:inline">Assign audit</span></button>}
                      <button data-testid="invite-by-email-button" onClick={() => setInviteOpen(true)}
                        className="btn-pill btn-pill-primary text-[11px]"><UserPlus className="w-3 h-3" /> <span className="hidden sm:inline">Invite</span></button>
                      <button data-testid="copy-team-code" onClick={() => { navigator.clipboard.writeText(selected.code); toast.success("Code copied"); }}
                        className="btn-pill btn-pill-ghost text-[11px]"><Copy className="w-3 h-3" /> {selected.code}</button>
                    </>
                  ) : selected.i_requested ? (
                    <span className="text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full bg-[#d6a900] text-white">Access requested</span>
                  ) : (
                    <RequestAccessButton hub={selected} onRequested={loadAll} />
                  )}
                </div>
              </div>
              {selected.i_am_member && (
                <div className="mt-3 -mx-3 md:mx-0 px-3 md:px-0 flex items-center gap-1 border-b border-transparent overflow-x-auto scrollbar-thin">
                  {TABS.map((t) => {
                    const Icon = t.icon;
                    const on = tab === t.k;
                    return (
                      <button key={t.k} data-testid={`hub-tab-${t.k}`} onClick={() => setTab(t.k)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold rounded-t-md border-b-2 whitespace-nowrap ${on ? "border-[#ff4500] text-[#ff4500]" : "border-transparent text-[#1a1a1a]/55 hover:text-[#1a1a1a]"}`}>
                        <Icon className="w-3 h-3" /> {t.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </header>

            {!selected.i_am_member ? (
              <div className="flex-1 flex items-center justify-center hatched p-6">
                <div className="bg-white border border-[#1a1a1a]/20 p-8 text-center max-w-md rounded-2xl">
                  <Users className="w-8 h-8 text-[#ff4500] mx-auto" />
                  <div className="font-display font-black text-xl mt-3">Join {selected.name} to see what&apos;s happening.</div>
                  <div className="text-sm text-[#1a1a1a]/70 mt-1">Request access — the hub Admin or a Controller will review.</div>
                  <div className="mt-3"><RequestAccessButton hub={selected} onRequested={loadAll} /></div>
                </div>
              </div>
            ) : (
              <>
                {tab === "chat" && <ChatPane hub={selected} user={user} />}
                {tab === "analytics" && <AnalyticsPane hub={selected} />}
                {tab === "assignments" && <AssignmentsPane hub={selected} canAssign={canAssign} />}
              </>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center hatched">
            <div className="bg-white border border-[#1a1a1a]/20 p-8 text-center max-w-sm">
              <Users className="w-8 h-8 text-[#ff4500] mx-auto" />
              <div className="font-display font-black text-xl mt-3">No hubs in your organization yet</div>
              <div className="text-sm text-[#1a1a1a]/70 mt-1">Create one to start.</div>
            </div>
          </div>
        )}
      </div>

      <CreateTeamDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(t) => { loadAll(); setSelected({ ...t, i_am_member: true, members_count: 1 }); }} />
      <JoinTeamDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={() => loadAll()} />
      {selected && <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} team={selected} />}
      {selected && <AssignAuditDialog open={assignOpen} onClose={() => setAssignOpen(false)} team={selected} />}
    </div>
  );
}

function RequestAccessButton({ hub, onRequested }) {
  const submit = async () => {
    try {
      const r = await api.post(`/teams/${hub.id}/request-access`, { reason: "" });
      if (r.data.status === "already_member") toast.info("You're already a member.");
      else toast.success("Access request sent. Hub Admin will review.");
      onRequested?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <button data-testid={`request-access-${hub.id}`} onClick={submit}
      className="btn-pill btn-pill-primary text-[11px]"><Mail className="w-3 h-3" /> Request access</button>
  );
}

// --- Chat pane with 1-level threading ----------------------------------
function ChatPane({ hub, user }) {
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const scrollRef = useRef(null);

  const fetchMsgs = useCallback(async () => {
    try {
      const r = await api.get(`/chat/${hub.id}`);
      setMsgs(r.data || []);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 30);
    } catch (e) { /* ignore */ }
  }, [hub.id]);

  useEffect(() => {
    fetchMsgs();
    const t = setInterval(fetchMsgs, 5000);
    return () => clearInterval(t);
  }, [fetchMsgs]);

  const send = async () => {
    if (!draft.trim()) return;
    try {
      const r = await api.post("/chat", {
        team_id: hub.id, message: draft.trim(),
        parent_id: replyTo?.id || null,
        signature_name: user?.name,
      });
      setMsgs((m) => [...m, r.data]);
      setDraft("");
      setReplyTo(null);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 30);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Group: roots in order, replies grouped under each root
  const roots = msgs.filter((m) => !m.parent_id);
  const replyMap = msgs.reduce((acc, m) => {
    if (m.parent_id) {
      acc[m.parent_id] = acc[m.parent_id] || [];
      acc[m.parent_id].push(m);
    }
    return acc;
  }, {});

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-auto scrollbar-thin p-3 md:p-6 space-y-3 md:space-y-4" data-testid="hub-chat-pane">
        {roots.length === 0 && <div className="hatched py-12 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">No messages yet — post the first hub-wide update.</div>}
        {roots.map((m) => {
          const mine = m.user_id === user?.id;
          const replies = replyMap[m.id] || [];
          return (
            <div key={m.id} data-testid={`hub-msg-${m.id}`} className="bg-white border border-[#1a1a1a]/15 rounded-2xl">
              <MessageRow m={m} mine={mine} onReply={() => setReplyTo(m)} isRoot />
              {replies.length > 0 && (
                <div className="border-t border-[#1a1a1a]/08 bg-[#fafaf8] rounded-b-2xl pl-6 pr-3 py-2 space-y-1">
                  {replies.map((r) => (
                    <MessageRow key={r.id} m={r} mine={r.user_id === user?.id} onReply={() => setReplyTo(m)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="bg-white border-t border-[#1a1a1a]/15 p-3">
        {replyTo && (
          <div className="mb-2 flex items-center gap-2 text-[11px] bg-[#fff1ea] border border-[#ff4500]/20 rounded-md px-2 py-1" data-testid="reply-banner">
            <CornerDownRight className="w-3 h-3 text-[#ff4500]" />
            Replying to <b>{replyTo.user_name}</b> · <span className="truncate max-w-[300px] text-[#1a1a1a]/70">{replyTo.message.slice(0, 80)}</span>
            <button onClick={() => setReplyTo(null)} className="ml-auto text-[#1a1a1a]/55 hover:text-[#1a1a1a]" data-testid="reply-cancel">×</button>
          </div>
        )}
        <div className="flex gap-2">
          <input data-testid="chat-input" value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
            placeholder={replyTo ? "Reply in thread…" : `Message everyone in ${hub.name}…`}
            className="flex-1 bg-white border border-[#1a1a1a]/30 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a]" />
          <button data-testid="chat-send-button" onClick={send}
            className="bg-[#ff4500] text-white rounded-md px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#cc3500] inline-flex items-center gap-1">
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </div>
        <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
          Every message is auto-signed and dated for compliance · this channel is hub-wide (NOT private)
        </div>
      </div>
    </>
  );
}

function MessageRow({ m, mine, onReply, isRoot }) {
  const sig = m.signature_name || m.user_name;
  return (
    <div className={`p-3 ${isRoot ? "" : ""}`}>
      <div className={`text-[10px] font-mono uppercase tracking-wider ${mine ? "text-[#ff4500]" : "text-[#1a1a1a]/60"} flex items-center gap-2 flex-wrap`}>
        <span className="font-bold">{m.user_name}</span>
        {m.user_title && <span className="text-[#1a1a1a]/55">· {m.user_title}</span>}
        <span className="text-[#1a1a1a]/55">· {new Date(m.created_at).toLocaleString()}</span>
        <span className="text-[#1a1a1a]/40">· signed as {sig}</span>
      </div>
      <div className="text-sm leading-snug mt-1 whitespace-pre-wrap text-[#1a1a1a]">{m.message}</div>
      {isRoot && (
        <button onClick={onReply} data-testid={`reply-${m.id}`}
          className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/55 hover:text-[#ff4500]">
          <CornerDownRight className="w-3 h-3" /> Reply in thread
        </button>
      )}
    </div>
  );
}

// --- Analytics pane (uses /api/hubs/{id}/pulse) ------------------------
function AnalyticsPane({ hub }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/hubs/${hub.id}/pulse`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Failed"));
  }, [hub.id]);
  if (err) return <div className="p-6"><div className="bg-[#fff1ea] border border-[#ff4500]/30 rounded-xl p-4 text-[12px]" data-testid="hub-analytics-error">{err}</div></div>;
  if (!data) return <div className="p-6 font-mono text-[11px] uppercase tracking-wider text-[#1a1a1a]/55">Loading…</div>;
  return (
    <div className="flex-1 overflow-auto scrollbar-thin p-6 space-y-4" data-testid="hub-analytics-pane">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={AlertTriangle} label="Overdue" value={data.overdue_total} tone="#ff4500" />
        <Kpi icon={CheckCircle2} label="Completed 7d" value={data.completed_last_week} tone="#1a8754" />
        <Kpi icon={Clock} label="Open now" value={data.open_now_total} />
        <Kpi icon={Users} label="Members" value={data.members_count} />
      </div>
      <div className="bg-white border border-[#1a1a1a]/15 rounded-2xl p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// At-risk · top 3 overdue</div>
        <ul className="divide-y divide-[#1a1a1a]/08">
          {(data.at_risk || []).length === 0 && <li className="py-2 text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">No one overdue — clean week.</li>}
          {(data.at_risk || []).map((p) => (
            <li key={p.user_id} className="py-2 flex items-center gap-2 text-[12px]">
              <Link to={`/app/people/${p.user_id}/analytics`} className="font-display font-bold hover:text-[#ff4500] flex-1 truncate">{p.name}</Link>
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded-full bg-[#fff1ea] text-[#ff4500]">{p.overdue} overdue</span>
            </li>
          ))}
        </ul>
      </div>
      <Link to="/app/pulse" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a] hover:text-[#ff4500]" data-testid="open-full-pulse">
        Open full Hub Pulse <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white border border-[#1a1a1a]/15 rounded-2xl p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff4500] flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-display font-black text-2xl mt-1" style={{ color: tone || "#1a1a1a" }}>{value ?? "—"}</div>
    </div>
  );
}

// --- Assignments pane -------------------------------------------------
function AssignmentsPane({ hub, canAssign }) {
  const [rows, setRows] = useState([]);
  const reload = useCallback(() => {
    api.get(`/hubs/${hub.id}/assignments`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [hub.id]);
  useEffect(() => { reload(); }, [reload]);
  const remove = async (id) => {
    try { await api.delete(`/assignments/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="flex-1 overflow-auto scrollbar-thin p-6" data-testid="hub-assignments-pane">
      {rows.length === 0 ? (
        <div className="hatched py-12 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
          No audits/checklists assigned to this hub yet.{canAssign && " Use the Assign audit button above."}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((a) => (
            <li key={a.id} className="bg-white border border-[#1a1a1a]/15 rounded-xl p-3 flex items-center gap-3" data-testid={`assignment-${a.id}`}>
              <ShieldCheck className="w-4 h-4 text-[#ff4500]" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm truncate">{a.template_name}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
                  {a.recurrence !== "none" ? `${a.recurrence} recurring` : "one-time"} · due {a.due_date || "—"} · created by {a.created_by_name}
                </div>
              </div>
              {canAssign && (
                <button onClick={() => remove(a.id)} data-testid={`assignment-remove-${a.id}`}
                  className="text-[10px] uppercase tracking-wider font-bold text-[#ff4500] hover:underline">Remove</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AssignAuditDialog({ open, onClose, team }) {
  const [templates, setTemplates] = useState([]);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ template_id: "", recurrence: "none", due_date: "", assignee_user_ids: [], assignee_team_ids: [], note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/checklist-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get("/users/visible-to-me").then((r) => setPeople(r.data || [])).catch(() => {});
    setForm({ template_id: "", recurrence: "none", due_date: "", assignee_user_ids: [], assignee_team_ids: [team.id], note: "" });
  }, [open, team.id]);
  const toggleUser = (id) => {
    const arr = form.assignee_user_ids;
    setForm({ ...form, assignee_user_ids: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };
  const submit = async () => {
    if (!form.template_id) return toast.error("Pick a checklist/audit template");
    setBusy(true);
    try {
      await api.post("/assignments", form);
      toast.success("Assigned");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-[#ff4500]" /> Assign audit to {team.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Template</label>
            <select data-testid="assign-template" value={form.template_id}
              onChange={(e) => setForm({ ...form, template_id: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
              <option value="">— pick a checklist/audit —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Recurrence</label>
              <select data-testid="assign-recurrence" value={form.recurrence}
                onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                <option value="none">One-time</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">First due date</label>
              <input type="date" data-testid="assign-due-date" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Assign to individuals (optional)</label>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-auto">
              {people.map((u) => {
                const on = form.assignee_user_ids.includes(u.id);
                return (
                  <button key={u.id} type="button" data-testid={`assign-user-${u.id}`}
                    onClick={() => toggleUser(u.id)}
                    className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                    {u.name}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-1">
              {form.recurrence !== "none" ? `Will recur ${form.recurrence} — every assignee gets a fresh run on each cycle.` : "One-time assignment to selected people + the whole hub."}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Note (optional)</label>
            <input data-testid="assign-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="What's the priority? Any context?"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="assign-submit" onClick={submit} disabled={busy || !form.template_id} className="btn-pill btn-pill-primary disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {busy ? "Assigning…" : "Assign"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Invite / Create / Join existing dialogs ---------------------------
function InviteDialog({ open, onClose, team }) {
  const [email, setEmail] = useState("");
  const submit = async () => {
    if (!email) return toast.error("Email required");
    try {
      const r = await api.post(`/teams/${team.id}/invite`, { email });
      if (r.data.status === "added") toast.success(`${r.data.user.name} added`);
      else if (r.data.status === "already_member") toast.info("Already a member");
      else toast.success("Invite sent (MOCKED)");
      setEmail("");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#ff4500]" /> Invite to {team.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="teammate@plant.com"
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
        </div>
        <DialogFooter>
          <button data-testid="invite-submit" onClick={submit} className="btn-pill btn-pill-primary">
            <Send className="w-3.5 h-3.5" /> Invite
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateTeamDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const submit = async () => {
    if (!name) return toast.error("Name required");
    const r = await api.post("/teams", { name, description });
    toast.success("Hub created");
    onCreated?.(r.data);
    setName(""); setDescription("");
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-wider">Create hub</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input data-testid="create-team-name-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Production Hub" className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          <input data-testid="create-team-desc-input" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Description"
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
        </div>
        <DialogFooter>
          <button data-testid="create-team-submit" onClick={submit} className="btn-pill btn-pill-primary">Create</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JoinTeamDialog({ open, onClose, onJoined }) {
  const [code, setCode] = useState("");
  const submit = async () => {
    if (!code) return toast.error("Code required");
    try {
      const r = await api.post("/teams/join", { code });
      toast.success(`Joined ${r.data.name}`);
      onJoined?.(r.data);
      setCode("");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-wider">Join hub by code</DialogTitle>
        </DialogHeader>
        <input data-testid="join-team-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABCD1234"
          className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm font-mono uppercase tracking-wider" />
        <DialogFooter>
          <button data-testid="join-team-submit" onClick={submit} className="btn-pill btn-pill-primary">Join</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
